import { useState, useEffect, KeyboardEvent } from 'react';
import { Laptop, Smartphone, EllipsisVertical } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { SessionWithActivitiesResource } from '@clerk/types';
import { useUser, useSession } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SocialConnections } from './components/social-connections';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';

import { AppTitle } from '@/components/app-header/app-header';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbSeparator,
  BreadcrumbPage
} from '@/components/ui/breadcrumb';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EditableAvatar } from '@/components/editable-avatar';

interface IDeviceDetails {
  id?: string;
  name?: string;
  deviceType?: string;
  lastActive?: string;
  isCurrentDevice?: boolean;
  browserName?: string;
  ipAddress?: string;
  location?: string;
  isMobile?: boolean;
  sessionId?: string;
}

interface CustomError {
  errors: { longMessage: string | null }[];
}

const formSchema = z.object({
  firstName: z.string().min(1, 'First Name is required'),
  lastName: z.string().min(1, 'Last Name is required')
});

export function ProfileSettings() {
  const { user } = useUser();
  const { toast } = useToast();
  const { session: currentSession } = useSession();
  const [devices, setDevices] = useState<IDeviceDetails[] | []>([]);
  const [devicesErrorMessage, setDevicesErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [removePhotoLoading, setRemovePhotoLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? ''
    },
    mode: 'onChange'
  });
  // Avatar handlers
  const handleAvatarChange = async (image: string) => {
    try {
      setPhotoLoading(true);
      const imageBlob = await fetch(image).then((r) => r.blob());
      await user?.setProfileImage({ file: imageBlob });
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update profile image',
        variant: 'destructive'
      });
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleAvatarRemove = async () => {
    try {
      setRemovePhotoLoading(true);
      await user?.setProfileImage({ file: null });
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to remove profile image',
        variant: 'destructive'
      });
    } finally {
      setRemovePhotoLoading(false);
    }
  };

  const onSubmit = async (input: z.infer<typeof formSchema>) => {
    try {
      setIsSaving(true);
      await user?.reload();
      await user?.update({
        firstName: input.firstName,
        lastName: input.lastName
      });
      toast({
        title: 'Profile updated successfully!',
        variant: 'default'
      });
    } catch (error) {
      const typedError = error as CustomError;
      toast({
        title: 'Error',
        description: typedError.errors[0].longMessage ?? 'Something went wrong',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onError = () => {
    toast({
      title: 'Error',
      description: 'Please fill out all the required fields.',
      variant: 'destructive'
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement | HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const logoutDevice = async (deviceInfo: IDeviceDetails) => {
    try {
      const userDevices = await user?.getSessions();
      const session = userDevices?.find((e) => e.id === deviceInfo.sessionId);
      await session?.revoke();
    } catch (error) {
      const typedError = error as CustomError;
      toast({
        title: 'Error',
        description: typedError.errors[0].longMessage ?? 'Something went wrong',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteUser = async () => {
    try {
      await user?.delete();
    } catch (error) {
      const typedError = error as CustomError;
      toast({
        title: 'Error',
        description: typedError.errors[0].longMessage ?? 'Something went wrong',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const userDevices = await user?.getSessions();
        if (userDevices) {
          const deviceDetails = userDevices.map((session: SessionWithActivitiesResource) => ({
            id: session.latestActivity.id,
            name: session.latestActivity.browserName ?? 'Unknown Device',
            deviceType: session.latestActivity.deviceType,
            lastActive: new Date(session.lastActiveAt).toLocaleString(),
            isCurrentDevice: currentSession?.id === session.id,
            browserName: session.latestActivity.browserName,
            ipAddress: session.latestActivity.ipAddress,
            location: `${session.latestActivity.city ?? 'Unknown City'}, ${session.latestActivity.country ?? 'Unknown Country'}`,
            isMobile: session.latestActivity.isMobile,
            sessionId: session.id
          }));
          setDevices(deviceDetails);
        }
      } catch (_error) {
        setDevicesErrorMessage('Failed to load devices.');
      }
    };
    void fetchDevices();
  }, [user]);

  return (
    <>
      <AppTitle>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="text-base">Settings</BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-base">Personal Profile</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </AppTitle>
      <div className="max-w-[800px] mx-auto space-y-6">
        {/* Profile Header */}
        <div className="flex justify-between items-center bg-card/50 border rounded-lg p-3.5">
          <div className="flex items-center gap-3">
            <EditableAvatar
              image={user?.imageUrl}
              hasUploadedImage={Boolean(user?.hasImage)}
              name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`}
              size="md"
              onImageChange={handleAvatarChange}
              onImageRemove={handleAvatarRemove}
              isLoading={photoLoading || removePhotoLoading}
              disabled={false}
            />
            <div>
              <h1 className="text-xl font-semibold">
                {user?.firstName} {user?.lastName}
              </h1>
              <p className="text-sm text-muted-foreground">
                {user?.primaryEmailAddress?.emailAddress}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="submit" form="profile-form" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <EllipsisVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive-foreground"
                  onClick={() => {
                    void handleDeleteUser();
                  }}
                >
                  Delete Account
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Form {...form}>
          <form
            id="profile-form"
            onSubmit={(e) => {
              void form.handleSubmit(onSubmit, onError)(e);
            }}
            onKeyDown={handleKeyDown}
          >
            <div className="space-y-4">
              <CardTitle className="text-lg">Identity</CardTitle>
              <div className="bg-card/50 border rounded-lg p-4">
                <div className="md:grid-cols-2 grid-cols-1 grid gap-6 pt-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormLabel className="text-foreground">First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your first name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormLabel className="text-foreground">Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your last name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
          </form>
        </Form>

        <div className="space-y-4">
          <CardTitle className="text-lg">Email</CardTitle>
          <div className="bg-card/50 border rounded-lg p-4">
            <div className="space-y-2">
              {user?.emailAddresses.map((emailObj) => (
                <div
                  key={emailObj.id}
                  className="flex justify-between items-center py-2.5 border-b last:border-b-0"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{emailObj.emailAddress}</span>
                    {emailObj.id === user.primaryEmailAddressId && (
                      <Badge variant="secondary">Primary</Badge>
                    )}
                    {emailObj.id !== user.primaryEmailAddressId &&
                      emailObj.verification.status === 'unverified' && (
                        <Badge variant="outline" className="text-xs">
                          {emailObj.verification.status.charAt(0).toUpperCase() +
                            emailObj.verification.status.slice(1)}
                        </Badge>
                      )}
                    {emailObj.id !== user.primaryEmailAddressId &&
                      emailObj.verification.status === 'expired' && (
                        <Badge variant="destructive" className="text-xs">
                          {emailObj.verification.status.charAt(0).toUpperCase() +
                            emailObj.verification.status.slice(1)}
                        </Badge>
                      )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <SocialConnections />

        <div className="space-y-4">
          <CardTitle className="text-lg">Devices</CardTitle>
          <div className="bg-card/50 border rounded-lg p-4">
            <div className="space-y-3">
              {devicesErrorMessage && (
                <div className="bg-destructive/10 text-destructive-foreground p-3 rounded-lg">
                  {devicesErrorMessage}
                </div>
              )}

              {devices.length > 0 ? (
                devices.map((device: IDeviceDetails) => (
                  <div
                    key={device.id}
                    className="flex items-start border-b last:border-0 pb-4 last:pb-0"
                  >
                    <div className="text-muted-foreground">
                      {device.isMobile ? <Smartphone /> : <Laptop />}
                    </div>
                    <div className="flex-1 ml-3">
                      <div className="flex items-center">
                        <h3 className="font-medium">{device.deviceType}</h3>
                        {device.isCurrentDevice && (
                          <Badge className="bg-sky hover:bg-sky text-sky-foreground ml-2">
                            This Device
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm">{device.browserName}</p>
                      <p className="text-muted-foreground text-sm break-all">
                        {device.ipAddress} ({device.location})
                      </p>
                      <p className="text-muted-foreground text-sm">{device.lastActive}</p>
                    </div>
                    {!device.isCurrentDevice && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <EllipsisVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive-foreground"
                            onClick={() => {
                              void logoutDevice(device);
                            }}
                          >
                            Log out
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground">No devices found.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
